'use client';

import React, { type ReactNode } from 'react';
import type { PressFillProps } from './primitives.types';

/*
  A cell's fill, which shrinks and can brighten while a finger is on it, revealing a darker inset
  underneath.

  Its own primitive rather than part of a board because it's a *transition between two resting
  states*, which the keyframe specs in ./animation deliberately can't express — those describe a
  motion that plays to completion, and a press has to be interruptible: the finger moves to the next
  cell and this one springs back mid-way.

  The indent is painted, not borrowed: the backing is this component's own layer and the colour
  shrinks inside it, so the effect doesn't depend on what happens to be behind the cell. A board whose
  cells are butted together — Mosaic's — has no gap to reveal, and scaling the cell itself would tear
  the grid.
*/

const FILL_PARENT: React.CSSProperties = { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 };

export const PressFill: React.FC<PressFillProps> = ({
  pressed,
  fill,
  pressedFill,
  backing,
  pressedScale,
  duration,
  style,
  children,
}) => (
  <div style={{ ...(style as React.CSSProperties), position: 'relative', backgroundColor: backing }}>
    <div
      style={{
        ...FILL_PARENT,
        borderRadius: style?.borderRadius,
        borderTopLeftRadius: style?.borderTopLeftRadius,
        borderTopRightRadius: style?.borderTopRightRadius,
        borderBottomLeftRadius: style?.borderBottomLeftRadius,
        borderBottomRightRadius: style?.borderBottomRightRadius,
        backgroundColor: pressed ? (pressedFill ?? fill) : fill,
        transform: pressed ? `scale(${pressedScale})` : undefined,
        transition: `transform ${duration}ms ease, background-color ${duration}ms ease`,
      }}
    />
    {children}
  </div>
);
